// Namespace import: the root tsconfig uses the classic JSX runtime, which needs React in scope
import * as React from 'react'
import { useEffect, useInsertionEffect, useState, type CSSProperties } from 'react'

/** Conversional brand orange — the backoffice default. Tenant-facing UIs pass their own brand color. */
export const CONVERSIONAL_ORANGE = '#fa7f46'

const SIZES = { small: 16, default: 32, large: 48 } as const

export type SpinnerProps = {
  /** Pixel size, or a preset: small 16, default 32, large 48 */
  size?: number | keyof typeof SIZES
  /** Any CSS color. Defaults to currentColor, so it follows the surrounding text color. */
  color?: string
  /** Announced by screen readers; shown below the spinner when showLabel is set */
  label?: string
  showLabel?: boolean
  /** Hide from assistive tech, e.g. inside a button that already says "Booking…" */
  decorative?: boolean
  /** Only appear after this many ms, so fast loads don't flash a spinner */
  delay?: number
  className?: string
  style?: CSSProperties
}

// The "C" from the Conversional logo (backoffice/public/logo.svg) at its exact proportions, 36 tall in a 48×48 box,
// sitting off centre the way the logo places it in its tile
const C_PATH = 'M34.32 8.21H25.63A15.79 15.79 0 0 0 25.63 39.79H34.32'
const C_STROKE = 4.42

const STYLE_ID = 'cv-spinner-styles'
const CSS = `
.cv-spinner{display:inline-flex;flex-direction:column;align-items:center;gap:8px;line-height:1}
.cv-spinner svg{display:block;overflow:visible}
.cv-spinner__track{opacity:.16}
/* gap (110) longer than the path (100): no rounding sliver when fully erased */
.cv-spinner__stroke{stroke-dasharray:100 110;stroke-dashoffset:100;animation:cv-spinner-draw 1.8s infinite}
@keyframes cv-spinner-draw{
  0%{stroke-dashoffset:100;animation-timing-function:cubic-bezier(.65,0,.35,1)}
  45%{stroke-dashoffset:0}
  55%{stroke-dashoffset:0;animation-timing-function:cubic-bezier(.65,0,.35,1)}
  100%{stroke-dashoffset:-110}
}
.cv-spinner__label{font-size:13px;opacity:.7}
.cv-spinner__sr{position:absolute;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;border:0}
@media (prefers-reduced-motion:reduce){
  .cv-spinner__stroke{stroke-dasharray:none;stroke-dashoffset:0;animation:cv-spinner-pulse 1.8s ease-in-out infinite}
  @keyframes cv-spinner-pulse{0%,100%{opacity:.35}50%{opacity:1}}
}`

function useSpinnerStyles() {
  useInsertionEffect(() => {
    if (document.getElementById(STYLE_ID)) return
    const style = document.createElement('style')
    style.id = STYLE_ID
    style.textContent = CSS
    document.head.appendChild(style)
  }, [])
}

export function Spinner({
  size = 'default',
  color = 'currentColor',
  label = 'Loading',
  showLabel = false,
  decorative = false,
  delay = 0,
  className,
  style,
}: SpinnerProps) {
  useSpinnerStyles()
  const [visible, setVisible] = useState(delay <= 0)

  useEffect(() => {
    if (delay <= 0) return
    const timer = setTimeout(() => setVisible(true), delay)
    return () => clearTimeout(timer)
  }, [delay])

  if (!visible) return null

  const px = typeof size === 'number' ? size : SIZES[size]
  const a11y = decorative ? { 'aria-hidden': true } : { role: 'status', 'aria-live': 'polite' as const }

  return (
    <span className={className ? `cv-spinner ${className}` : 'cv-spinner'} style={style} {...a11y}>
      <svg width={px} height={px} viewBox="0 0 48 48" fill="none" style={{ color }} aria-hidden="true">
        <path className="cv-spinner__track" d={C_PATH} stroke="currentColor" strokeWidth={C_STROKE} />
        <path className="cv-spinner__stroke" d={C_PATH} stroke="currentColor" strokeWidth={C_STROKE} pathLength={100} />
      </svg>
      {!decorative && <span className={showLabel ? 'cv-spinner__label' : 'cv-spinner__sr'}>{label}</span>}
    </span>
  )
}
